import { memo, useCallback, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { PermissionButton } from 'src/routes/components';

import { useManualIssue } from './hooks/useManualIssue';
import BatchIssueDialog from './components/BatchIssueDialog';
import type {
  BatchUserEntry,
  ManualIssueGrant,
  ManualIssueStats,
  ReasonCategory,
  TranslateFn,
} from './types';
import {
  formatDateTime,
  formatInt,
  generateRequestId,
  getErrorMessage,
  shortenWallet,
} from './utils';

const I18N_PREFIX = 'module.worldCup.manualIssue';

const tMI = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

export const REASON_OPTIONS: { value: ReasonCategory; labelKey: string }[] = [
  { value: 'customer_support', labelKey: 'reason.customer_support' },
  { value: 'event_reward', labelKey: 'reason.event_reward' },
  { value: 'risk_control', labelKey: 'reason.risk_control' },
  { value: 'operator_manual', labelKey: 'reason.operator_manual' },
  { value: 'bold_prediction', labelKey: 'reason.bold_prediction' },
  { value: 'other', labelKey: 'reason.other' },
];

const REASON_COLOR: Record<
  string,
  'default' | 'primary' | 'success' | 'warning' | 'info' | 'error'
> = {
  customer_support: 'info',
  event_reward: 'success',
  risk_control: 'warning',
  bold_prediction: 'primary',
  other: 'default',
};

const DAILY_CAP = 100;

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const {
    stats,
    grants,
    recentGrants,
    pageInfo,
    page,
    pageCount,
    search,
    loadingStats,
    loadingGrants,
    submitting,
    exporting,
    refetchStats,
    refetchGrants,
    setPage,
    setSearch,
    createGrant,
    batchCreateGrant,
    exportGrants,
  } = useManualIssue();

  const [userId, setUserId] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [count, setCount] = useState<string>('10');
  const [reason, setReason] = useState<ReasonCategory>('customer_support');
  const [note, setNote] = useState('');
  const [batchOpen, setBatchOpen] = useState(false);

  const handleSubmit = useCallback(async () => {
    const targetUid = userId.trim();
    const targetWallet = walletAddress.trim();
    const numericCount = Math.floor(Number(count));

    if (!targetUid && !targetWallet) {
      notify(tMI(t, 'notify.targetRequired'), { type: 'warning' });
      return;
    }

    if (!Number.isFinite(numericCount) || numericCount <= 0) {
      notify(tMI(t, 'notify.countInvalid'), { type: 'warning' });
      return;
    }

    if (numericCount > DAILY_CAP) {
      if (!window.confirm(tMI(t, 'confirm.exceedDailyCap', { cap: DAILY_CAP }))) {
        return;
      }
    }

    try {
      await createGrant({
        requestId: generateRequestId(),
        userId: targetUid || undefined,
        walletAddress: targetWallet || undefined,
        count: numericCount,
        reasonCategory: reason,
        note: note.trim() || undefined,
      });

      notify(tMI(t, 'notify.createSuccess'), { type: 'success' });

      setUserId('');
      setWalletAddress('');
      setCount('10');
      setNote('');

      setPage(1);
      await Promise.all([refetchStats(), refetchGrants()]);
    } catch (err) {
      notify(getErrorMessage(err) || tMI(t, 'notify.createFailed'), { type: 'error' });
    }
  }, [
    count,
    createGrant,
    note,
    notify,
    reason,
    refetchGrants,
    refetchStats,
    setPage,
    t,
    userId,
    walletAddress,
  ]);

  const handleExport = useCallback(async () => {
    try {
      await exportGrants();
    } catch (err) {
      notify(getErrorMessage(err) || tMI(t, 'notify.exportFailed'), { type: 'error' });
    }
  }, [exportGrants, notify, t]);

  const handleBatchSubmit = useCallback(
    async (batchNote: string, batchReason: ReasonCategory, users: BatchUserEntry[]) => {
      if (users.length === 0) {
        notify(tMI(t, 'notify.targetRequired'), { type: 'warning' });
        return;
      }
      try {
        const result = (await batchCreateGrant({
          requestId: generateRequestId(),
          reasonCategory: batchReason,
          note: batchNote || undefined,
          users,
        })) as { succeeded?: number; failed?: number; total?: number } | null;

        const total = Number(result?.total ?? users.length) || users.length;
        const succeeded = Number(result?.succeeded ?? 0) || 0;
        const failed = Number(result?.failed ?? Math.max(total - succeeded, 0)) || 0;
        const variables = { total, succeeded, failed };

        if (failed === 0) {
          notify(tMI(t, 'notify.batchSuccess', variables), { type: 'success' });
        } else if (succeeded === 0) {
          notify(tMI(t, 'notify.batchAllFailed', variables), { type: 'error' });
        } else {
          notify(tMI(t, 'notify.batchPartial', variables), { type: 'success' });
        }

        setBatchOpen(false);
        setPage(1);
        await Promise.all([refetchStats(), refetchGrants()]);
      } catch (err) {
        notify(getErrorMessage(err) || tMI(t, 'notify.batchFailed'), { type: 'error' });
      }
    },
    [batchCreateGrant, notify, refetchGrants, refetchStats, setPage, t]
  );

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tMI(t, 'title')}
        links={[{ name: t('resource.worldCup') }, { name: t('resource.worldCupManualIssue') }]}
        action={
          <Stack direction="row" spacing={1.5}>
            <PermissionButton resource="world-cup-voucher-manual-issue" action="write">
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:layers-outline" />}
                onClick={() => setBatchOpen(true)}
              >
                {tMI(t, 'actions.batchIssue')}
              </Button>
            </PermissionButton>
            <PermissionButton resource="world-cup-voucher-manual-issue" action="export">
              <Button
                variant="outlined"
                disabled={exporting}
                startIcon={<Iconify icon="eva:download-outline" />}
                onClick={handleExport}
              >
                {exporting ? t('common.saving') : tMI(t, 'actions.export')}
              </Button>
            </PermissionButton>
          </Stack>
        }
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tMI(t, 'description')}
      </Typography>

      {loadingStats && !stats ? (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
          <CircularProgress size={28} />
        </Stack>
      ) : (
        <Stack spacing={3}>
          <StatsRow stats={stats} t={t} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
              gap: 3,
            }}
          >
            <IssueVoucherForm
              userId={userId}
              walletAddress={walletAddress}
              count={count}
              reason={reason}
              note={note}
              submitting={submitting}
              onUserIdChange={setUserId}
              onWalletChange={setWalletAddress}
              onCountChange={setCount}
              onReasonChange={setReason}
              onNoteChange={setNote}
              onSubmit={handleSubmit}
              t={t}
            />

            <RecentRecords records={recentGrants} t={t} />
          </Box>

          <IssuanceRecords
            grants={grants}
            totalCount={pageInfo.totalCount}
            search={search}
            page={page}
            pageCount={pageCount}
            loading={loadingGrants}
            onSearchChange={setSearch}
            onPageChange={setPage}
            t={t}
          />
        </Stack>
      )}

      <BatchIssueDialog
        open={batchOpen}
        loading={submitting}
        onClose={() => setBatchOpen(false)}
        onSubmit={handleBatchSubmit}
      />
    </DashboardContent>
  );
};

const StatsRow = memo(({ stats, t }: { stats: ManualIssueStats; t: TranslateFn }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' },
      gap: 2,
    }}
  >
    <StatCard
      label={tMI(t, 'stats.issuedToday')}
      value={formatInt(stats.today.voucherCount)}
      sub={tMI(t, 'stats.entries', { count: stats.today.grantCount })}
      accent="success.main"
    />
    <StatCard
      label={tMI(t, 'stats.totalIssued')}
      value={formatInt(stats.allTime.voucherCount)}
      sub={tMI(t, 'stats.acrossUsers', { count: stats.allTime.distinctUsers })}
      accent="secondary.main"
    />
    <StatCard
      label={tMI(t, 'stats.lastIssuance')}
      value={stats.lastGrant ? formatInt(stats.lastGrant.count) : '--'}
      sub={stats.lastGrant ? formatDateTime(stats.lastGrant.createdAt) : '--'}
      accent="info.main"
    />
    <StatCard
      label={tMI(t, 'stats.issueStatus')}
      value={tMI(t, 'stats.ready')}
      sub={tMI(t, 'stats.recordedImmediately')}
      accent="success.main"
    />
  </Box>
));

StatsRow.displayName = 'StatsRow';

const StatCard = memo(
  ({
    label,
    value,
    sub,
    accent,
  }: {
    label: string;
    value: string;
    sub: string;
    accent: string;
  }) => (
    <Card sx={{ p: 3, borderLeft: 4, borderColor: accent }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
        {sub}
      </Typography>
    </Card>
  )
);

StatCard.displayName = 'StatCard';

const IssueVoucherForm = memo(
  ({
    userId,
    walletAddress,
    count,
    reason,
    note,
    submitting,
    onUserIdChange,
    onWalletChange,
    onCountChange,
    onReasonChange,
    onNoteChange,
    onSubmit,
    t,
  }: {
    userId: string;
    walletAddress: string;
    count: string;
    reason: ReasonCategory;
    note: string;
    submitting: boolean;
    onUserIdChange: (value: string) => void;
    onWalletChange: (value: string) => void;
    onCountChange: (value: string) => void;
    onReasonChange: (value: ReasonCategory) => void;
    onNoteChange: (value: string) => void;
    onSubmit: () => void;
    t: TranslateFn;
  }) => {
    const numericCount = Number(count);
    const countInvalid = !count.trim() || !Number.isFinite(numericCount) || numericCount <= 0;

    return (
      <Card sx={{ p: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6">{tMI(t, 'form.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {tMI(t, 'form.desc')}
            </Typography>
          </Box>
          <Chip label={tMI(t, 'form.ready')} color="success" size="small" />
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mt: 2,
          }}
        >
          <TextField
            size="small"
            fullWidth
            label={tMI(t, 'form.userId')}
            placeholder="uid_100238"
            value={userId}
            onChange={(event) => onUserIdChange(event.target.value)}
          />

          <TextField
            size="small"
            fullWidth
            label={tMI(t, 'form.wallet')}
            placeholder="0x..."
            value={walletAddress}
            onChange={(event) => onWalletChange(event.target.value)}
          />

          <TextField
            size="small"
            fullWidth
            required
            label={tMI(t, 'form.amount')}
            value={count}
            error={countInvalid}
            helperText={
              countInvalid
                ? t('ra.validation.required')
                : tMI(t, 'form.amountHint', { cap: DAILY_CAP })
            }
            onChange={(event) => {
              const cleaned = event.target.value.replace(/[^\d]/g, '');
              onCountChange(cleaned);
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{tMI(t, 'unit.ticket')}</InputAdornment>,
            }}
            inputProps={{ inputMode: 'numeric' }}
          />

          <TextField
            select
            size="small"
            fullWidth
            label={tMI(t, 'form.reason')}
            value={reason}
            onChange={(event) => onReasonChange(event.target.value as ReasonCategory)}
          >
            {REASON_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {tMI(t, option.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label={tMI(t, 'form.memo')}
          placeholder={tMI(t, 'form.memoPlaceholder')}
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          inputProps={{ maxLength: 500 }}
          sx={{ mt: 2 }}
        />

        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 3 }}>
          <PermissionButton resource="world-cup-voucher-manual-issue" action="write">
            <Button
              variant="contained"
              disabled={submitting || countInvalid}
              onClick={onSubmit}
              startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
            >
              {submitting ? t('common.saving') : tMI(t, 'form.submit')}
            </Button>
          </PermissionButton>
          <Typography variant="caption" color="text.secondary">
            {tMI(t, 'form.submitHint')}
          </Typography>
        </Stack>
      </Card>
    );
  }
);

IssueVoucherForm.displayName = 'IssueVoucherForm';

const RecentRecords = memo(({ records, t }: { records: ManualIssueGrant[]; t: TranslateFn }) => (
  <Card sx={{ p: 3 }}>
    <Typography variant="h6">{tMI(t, 'recent.title')}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
      {tMI(t, 'recent.desc')}
    </Typography>

    {records.length ? (
      <Stack spacing={1.5}>
        {records.map((record) => (
          <Paper key={record.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {record.userId || shortenWallet(record.walletAddress)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {shortenWallet(record.walletAddress) || '--'} ·{' '}
                  {tMI(t, `reason.${record.reasonCategory}`)}
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="success.main" fontWeight={700}>
                +{formatInt(record.count)} {tMI(t, 'unit.ticket')}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    ) : (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {tMI(t, 'recent.empty')}
        </Typography>
      </Stack>
    )}
  </Card>
));

RecentRecords.displayName = 'RecentRecords';

const IssuanceRecords = memo(
  ({
    grants,
    totalCount,
    search,
    page,
    pageCount,
    loading,
    onSearchChange,
    onPageChange,
    t,
  }: {
    grants: ManualIssueGrant[];
    totalCount: number;
    search: string;
    page: number;
    pageCount: number;
    loading: boolean;
    onSearchChange: (value: string) => void;
    onPageChange: (value: number) => void;
    t: TranslateFn;
  }) => (
    <Card sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">{tMI(t, 'records.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {tMI(t, 'records.desc', { count: totalCount })}
          </Typography>
        </Box>

        <TextField
          size="small"
          placeholder={tMI(t, 'records.searchPlaceholder')}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 320 }}
        />
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{tMI(t, 'records.col.id')}</TableCell>
              <TableCell>{tMI(t, 'records.col.time')}</TableCell>
              <TableCell>{tMI(t, 'records.col.user')}</TableCell>
              <TableCell align="right">{tMI(t, 'records.col.amount')}</TableCell>
              <TableCell>{tMI(t, 'records.col.reason')}</TableCell>
              <TableCell>{tMI(t, 'records.col.memo')}</TableCell>
              <TableCell align="right">{tMI(t, 'records.col.operator')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Stack alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </Stack>
                </TableCell>
              </TableRow>
            ) : grants.length ? (
              grants.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDateTime(row.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2" fontWeight={500}>
                        {row.userId || '--'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {shortenWallet(row.walletAddress) || '--'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`+${formatInt(row.count)}`}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tMI(t, `reason.${row.reasonCategory}`)}
                      color={REASON_COLOR[row.reasonCategory] ?? 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.note || '--'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {row.operator || '--'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7}>
                  <Stack alignItems="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {tMI(t, 'records.empty')}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pageCount > 1 ? (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => onPageChange(value)}
            color="primary"
            disabled={loading}
            shape="rounded"
          />
        </Stack>
      ) : null}
    </Card>
  )
);

IssuanceRecords.displayName = 'IssuanceRecords';
